import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  NumberIncrementStepper,
  NumberInputStepper,
  NumberInputField,
  NumberInput,
  Box,
  FormLabel,
  NumberDecrementStepper,
  useDisclosure,
  Text,
  VStack,
  HStack
} from '@chakra-ui/react'
import { FC, useState, useMemo } from 'react'
import {
  FaShoppingCart,
  FaCheckCircle,
  FaArrowRight,
  FaArrowLeft
} from 'react-icons/fa'
import { SiSap } from 'react-icons/si'

import useMrpScheduledPurchase from '@hook/mrp/use-scheduled-purchase'

import JustificationTable from '@component/Modules/Mrp/JustificationTable'
import Filter from '@component/Shared/Filter'

import { ILayoutActionSuppliers } from '@base/@types/components/Layout/Tools'

import LayoutToolsButton from '../Components/Button'

const ActionPurchaseCreateOrderSapSchedule: FC<ILayoutActionSuppliers> = () => {
  const {
    handleUpdateSapParams,
    paramsSap,
    exportDataSap,
    exportUnicDataSap,
    supplyMethod,
    discountSupply,
    alteredSkus,
    handleUpdateJustification,
    sendingOrder
  } = useMrpScheduledPurchase()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [valueDesconto, setDesconto] = useState('')
  const [step, setStep] = useState(1)
  // Tipo de envio escolhido no passo 1, executado ao confirmar as justificativas
  const [isUnico, setIsUnico] = useState(false)

  const isStep2ConfirmDisabled = useMemo(
    () => sendingOrder || alteredSkus.some(sku => !sku.justificativa),
    [alteredSkus, sendingOrder]
  )

  const handleOpenModal = () => {
    // Reabre sempre no passo 1 com o desconto atualmente aplicado
    setStep(1)
    setIsUnico(false)
    setDesconto(paramsSap.desconto ? paramsSap.desconto.toString() : '')
    onOpen()
  }

  const handleModalClose = () => {
    // Não fecha no meio do envio
    if (sendingOrder) return
    onClose()
    setStep(1)
  }

  const send = async (unico: boolean) => {
    const success = unico ? await exportUnicDataSap() : await exportDataSap()
    if (success) {
      setDesconto('')
      handleModalClose()
    }
  }

  // Com SKUs alterados exige justificativa (passo 2) antes de qualquer envio
  const handleProceed = (unico: boolean) => {
    if (sendingOrder) return
    setIsUnico(unico)
    if (alteredSkus.length > 0) {
      setStep(2)
      return
    }
    send(unico)
  }

  const handleConfirm = () => {
    if (isStep2ConfirmDisabled) return
    send(isUnico)
  }

  const validateSap = () => {
    if (
      discountSupply.metodoPagamentoId === paramsSap.metodoPagamento[0]?.value
    ) {
      if (Number(valueDesconto) < Number(discountSupply.desconto)) {
        setDesconto(discountSupply.desconto.toString())
      }
    }
  }
  const defaultDiscount = (value: any) => {
    if (discountSupply.metodoPagamentoId === value[0]?.value) {
      setDesconto(discountSupply.desconto.toString())
    }
  }
  return (
    <>
      <LayoutToolsButton label="Prosseguir" onClick={handleOpenModal} />
      <Modal
        isOpen={isOpen}
        onClose={handleModalClose}
        size={step === 1 ? '2xl' : '6xl'}
        scrollBehavior="inside"
        closeOnOverlayClick={!sendingOrder}
      >
        <ModalOverlay />
        <ModalContent bgColor="white" borderRadius="lg">
          <ModalHeader>
            <HStack spacing={3}>
              <HStack spacing={2}>
                <SiSap size="1.2em" color="#0073C6" />
                <FaShoppingCart color="#38a169" />
              </HStack>
              <Text>
                {step === 1
                  ? 'Criar Pedido de Compra SAP'
                  : 'Justificativa de Alteração'}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton isDisabled={sendingOrder} />

          <ModalBody py={6}>
            {step === 1 ? (
              <VStack spacing={6} align="stretch">
                <Box
                  p={6}
                  bg="white"
                  borderRadius="lg"
                  boxShadow="sm"
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <FormControl id="discount">
                    <FormLabel>Desconto (%)</FormLabel>
                    <NumberInput
                      value={valueDesconto}
                      onChange={value => setDesconto(value)}
                      onBlur={(event: any) => {
                        validateSap()
                        handleUpdateSapParams({
                          name: 'desconto',
                          value: event.target.value
                        })
                      }}
                    >
                      <NumberInputField borderColor="gray.300" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                  <Filter
                    label="Forma de Pagamento"
                    options={supplyMethod.map(paymentMethod => ({
                      label: `${paymentMethod.codigo} - ${paymentMethod.descricao}`,
                      value: paymentMethod.id
                    }))}
                    value={paramsSap.metodoPagamento}
                    isMulti
                    onChange={value => {
                      defaultDiscount(value)
                      handleUpdateSapParams({ name: 'metodoPagamento', value })
                    }}
                  />
                </Box>
              </VStack>
            ) : (
              <JustificationTable
                alteredSkus={alteredSkus}
                onUpdateJustification={handleUpdateJustification}
              />
            )}
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="lg">
            {step === 1 ? (
              <HStack>
                <Button
                  variant="ghost"
                  onClick={() => handleProceed(true)}
                  isDisabled={!supplyMethod || sendingOrder}
                  isLoading={sendingOrder && isUnico}
                >
                  Unico
                </Button>
                <Button
                  onClick={() => handleProceed(false)}
                  isDisabled={!supplyMethod || sendingOrder}
                  isLoading={sendingOrder && !isUnico}
                  colorScheme="green"
                  rightIcon={<FaArrowRight />}
                >
                  Prosseguir
                </Button>
              </HStack>
            ) : (
              <HStack>
                <Button
                  variant="ghost"
                  onClick={() => setStep(1)}
                  isDisabled={sendingOrder}
                  leftIcon={<FaArrowLeft />}
                >
                  Voltar
                </Button>
                <Button
                  colorScheme="green"
                  onClick={handleConfirm}
                  isDisabled={isStep2ConfirmDisabled}
                  isLoading={sendingOrder}
                  leftIcon={<FaCheckCircle />}
                >
                  {isUnico ? 'Confirmar (Único)' : 'Confirmar'}
                </Button>
              </HStack>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ActionPurchaseCreateOrderSapSchedule
